import { useAppSelector } from 'app/hooks';
import Button from 'blocks/Button';
import Row from 'blocks/Row';
import { PageTitle, Text } from 'blocks/Text';
import BadgeText from 'components/BadgeText';
import EmptyBox from 'components/EmptyBox';
import { TextField } from 'components/Form';
import Loading from 'components/Loading';
import Table from 'components/Table';
import { getDate, getTime } from 'helper/date';
import Paths from 'helper/paths';
import WithSidebar from 'layouts/WithSidebar';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { getProductBookingService } from 'services/productBooking';
import {
  productBookingLoadingState,
  productBookingState,
  setProductBooking,
  setProductBookingError,
  setProductBookingLoading,
} from 'store/ProductBooking/productBookingSlice';
import CanclePurchas from './CanclePurchas';
import ModalSearch from './ModalSearch';
import * as S from './productBooking';

const Booking = () => {
  const loading = useAppSelector(productBookingLoadingState);
  const booking = useAppSelector(productBookingState);
  const navigate = useNavigate();
  const DeliverDetailModal = useRef<any>();
  const [showModalSearch, setShowModalSearch] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [pageData, setPageData] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
  });
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    getBooking(pageData.currentPage, searchParams);
  }, [pageData.currentPage, searchParams]);

  const getBooking = async (page: number, search: any = {}) => {
    let searchString = Object.keys(searchParams)
      .map((key) => {
        if (!Array.isArray(searchParams[key])) return `${key}=${searchParams[key]}`;
        return searchParams[key].map((val) => `${key}[]=${val}`).join('&');
      })
      .join('&');
    const params = `?page=${page}&size=30&` + (searchString || '');
    dispatch(setProductBookingLoading());

    getProductBookingService(Number(page), params)
      .then((res) => {
        dispatch(
          setProductBooking({
            productBooking: res.data,
            pageMeta: res.pageMeta,
          })
        );
        setPageData(res.pageMeta);
      })
      .catch((err) => {
        dispatch(setProductBookingError({ err }));
      });
  };
  const getBadgeColor = (status: string) => {
    if (status === 'purchased') return 'success';
    if (status === 'deactive' || status === 'canceled') return 'error';
    return 'warn';
  };

  const next = () => {
    setPageData({
      ...pageData,
      currentPage: pageData.currentPage + 1,
    });
  };

  const prev = () => {
    setPageData({
      ...pageData,
      currentPage: pageData.currentPage - 1,
    });
  };

  return (
    <WithSidebar>
      <S.Products>
        <Row justify="space-between" align="center">
          <PageTitle>{t('pages.services.Products.sections.Booking.title')}</PageTitle>
          <S.ParentSearch>
            <TextField
              placeholder={t('pages.miles.view.sections.filters.fields.search.placeholder')}
              readonly
              onClick={() => setShowModalSearch(true)}
            />
            <Button onClick={() => navigate(Paths.Services.products().route)}>
              {t('pages.services.Products.sections.Card.btnBack')}
            </Button>
          </S.ParentSearch>
        </Row>
        {loading ? (
          <Loading />
        ) : (
          <>
            {booking.length ? (
              <>
                <S.ParentTable>
                  <Table
                    name="booking"
                    head={[
                      { text: t('pages.services.Products.sections.Booking.sections.headers.userName') },
                      { text: t('pages.services.Products.sections.Booking.sections.headers.FFpNumber') },
                      { text: t('pages.services.Products.sections.Booking.sections.headers.MerchantName') },
                      { text: t('pages.services.Products.sections.Booking.sections.headers.ProductName') },
                      { text: t('pages.services.Products.sections.Booking.sections.headers.RewardMilage') },
                      { text: t('pages.services.Products.sections.Booking.sections.headers.TraceNumber') },
                      { text: t('pages.services.Products.sections.Booking.sections.headers.Status') },
                      { text: t('pages.services.Products.sections.Booking.sections.headers.Date') },
                      { text: '' },
                    ]}
                    rows={booking.map((item, index) => [
                      {
                        content: (
                          <Row align="center">
                            {item.User.enFname} {item.User.enLname}
                          </Row>
                        ),
                      },
                      {
                        content: <Row align="center">{item.User.idNumber}</Row>,
                      },

                      {
                        content: <Row align="center">{item.Merchant.name}</Row>,
                      },
                      {
                        content: <Row align="center">{item.Product.name}</Row>,
                      },
                      {
                        content: <Row align="center">{item.rewardMilage}</Row>,
                      },
                      {
                        content: <Row align="center">{item.traceNumber}</Row>,
                      },
                      {
                        content: (
                          <Text color="success" size="small">
                            <BadgeText color={getBadgeColor(item.status)}>{item.status}</BadgeText>
                          </Text>
                        ),
                      },
                      {
                        content: `${getDate(item.updatedAt)}  ${getTime(item.updatedAt)}`,
                      },
                      {
                        content: item.status == 'purchased' && (
                          <Row justify="center">
                            <Button
                              color="danger"
                              onClick={() => {
                                DeliverDetailModal.current.openModal(item.id, index);
                              }}
                            >
                              {t('pages.services.Products.sections.Booking.sections.cancelPurchase.title')}
                            </Button>
                          </Row>
                        ),
                      },
                    ])}
                    page={pageData.currentPage}
                    total={pageData.totalItems}
                    next={next}
                    prev={prev}
                    limit={30}
                  />
                </S.ParentTable>
              </>
            ) : (
              <EmptyBox boxed title={t('pages.services.Products.sections.Booking.sections.emptyText')} />
            )}
          </>
        )}
      </S.Products>
      <ModalSearch
        open={showModalSearch}
        onClose={() => setShowModalSearch(false)}
        onSearch={(objVal: object) => {
          setSearchParams((prev) => ({ ...prev, ...objVal }));
        }}
        paramsObj={searchParams}
        onClear={() => {
          setSearchParams({});
        }}
      />
      <CanclePurchas ref={DeliverDetailModal} />
    </WithSidebar>
  );
};

export default Booking;
